import { z } from "zod";
import {
  emptyWhatsAppTemplateContent,
  whatsAppTemplateContentSchema,
  type WhatsAppTemplateContent,
} from "./whatsapp-template-content";
import type { MetaTemplateLanguage } from "./whatsapp-template-language";

export const WHATSAPP_TEMPLATE_CATEGORIES = [
  "MARKETING",
  "UTILITY",
  "AUTHENTICATION",
] as const;

export type WhatsAppTemplateCategory =
  (typeof WHATSAPP_TEMPLATE_CATEGORIES)[number];

/**
 * Authentication templates need Twilio's `whatsapp/authentication` content
 * type, which the compiler doesn't build yet.
 */
export const SELECTABLE_WHATSAPP_TEMPLATE_CATEGORIES = [
  "MARKETING",
  "UTILITY",
] as const satisfies readonly WhatsAppTemplateCategory[];

export function isWhatsAppTemplateCategory(
  value: string,
): value is WhatsAppTemplateCategory {
  return (WHATSAPP_TEMPLATE_CATEGORIES as readonly string[]).includes(value);
}

export function parseWhatsAppTemplateCategory(
  value: unknown,
): WhatsAppTemplateCategory | null {
  if (typeof value !== "string") return null;
  const upper = value.toUpperCase();
  return isWhatsAppTemplateCategory(upper) ? upper : null;
}

/**
 * Meta's category once it has approved the template, else the requested one,
 * which is what the next submission asks Meta for.
 */
export function effectiveWhatsAppTemplateCategory(
  locale:
    | {
        category?: string | null;
        requested_category?: string | null;
        approved_at?: string | Date | null;
      }
    | null
    | undefined,
): WhatsAppTemplateCategory | null {
  const requested = parseWhatsAppTemplateCategory(locale?.requested_category);
  if (!locale?.approved_at) return requested;
  return parseWhatsAppTemplateCategory(locale.category) ?? requested;
}

export const WHATSAPP_TEMPLATE_CATEGORY_LABELS: Record<
  WhatsAppTemplateCategory,
  string
> = {
  MARKETING: "Marketing",
  UTILITY: "Utility",
  AUTHENTICATION: "Authentication",
};

/**
 * The one status Hightouch owns: the template exists here and has never been
 * sent to the provider, or the provider content it had has been deleted.
 * Everything else in this column is the provider's own verdict.
 */
export const WHATSAPP_TEMPLATE_STATUS_UNSUBMITTED = "UNSUBMITTED";

/**
 * Meta's own template statuses.
 */
export const META_TEMPLATE_STATUSES = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "PAUSED",
  "DISABLED",
  "DELETED",
  "PENDING_DELETION",
  "LOCKED",
  "LIMIT_EXCEEDED",
  "IN_APPEAL",
  "FLAGGED",
  "REINSTATED",
  // TODO
  // "ARCHIVED",
  // "UNARCHIVED",
] as const;

export type WhatsAppTemplateStatus =
  | typeof WHATSAPP_TEMPLATE_STATUS_UNSUBMITTED
  | (typeof META_TEMPLATE_STATUSES)[number];

export function isWhatsAppTemplateStatus(
  value: string,
): value is WhatsAppTemplateStatus {
  return (
    value === WHATSAPP_TEMPLATE_STATUS_UNSUBMITTED ||
    (META_TEMPLATE_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * The statuses Meta still delivers on. REINSTATED restores a template Meta had
 * suppressed and FLAGGED is a quality warning that does not stop delivery, so
 * both send exactly as APPROVED does.
 *
 * Typed as strings rather than `WhatsAppTemplateStatus` because the column has
 * no CHECK and stores provider vocabulary verbatim: a value can reach it
 * before it reaches the union above.
 */
export const WHATSAPP_SENDABLE_STATUSES: readonly string[] = [
  "APPROVED",
  "REINSTATED",
  "FLAGGED",
];

/**
 * Membership rather than "not one of the bad ones": a status nobody has taught
 * us about refuses the send, which is the safe direction while Meta keeps
 * extending its vocabulary.
 */
export function isSendableWhatsAppTemplateStatus(
  status: string | null | undefined,
): boolean {
  return status != null && WHATSAPP_SENDABLE_STATUSES.includes(status);
}

export const DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE: MetaTemplateLanguage = "en_US";

export const DEFAULT_WHATSAPP_TEMPLATE_CATEGORY: WhatsAppTemplateCategory =
  "MARKETING";

/** The authored components, or an empty template when the row will not parse. */
export function readWhatsAppEditorContent(
  editorState: unknown,
): WhatsAppTemplateContent {
  const parsed = whatsAppTemplateContentSchema.safeParse(editorState);
  return parsed.success ? parsed.data : emptyWhatsAppTemplateContent();
}

export type WhatsAppTemplateLocaleTouchFields = {
  submitted_at: Date | string | null;
  provider_content_id: string | null;
  editor_state: unknown;
};

/** Whether nothing was ever written to the locale or sent for review. */
export function isUntouchedWhatsAppTemplateLocale(
  locale: WhatsAppTemplateLocaleTouchFields,
): boolean {
  const content = readWhatsAppEditorContent(locale.editor_state);
  return (
    locale.submitted_at === null &&
    locale.provider_content_id === null &&
    content.body.trim() === "" &&
    content.header === null &&
    content.footer === null &&
    content.buttons.length === 0
  );
}

/**
 * How a send fills one Meta position. `liquid` is always what renders for the
 * recipient; `kind` says what happens to the result:
 * - `text`: sent as rendered.
 * - `tracked_link`: rendered to the destination URL, then swapped for a short
 *   id, since the approved template already carries our redirect host.
 */
const whatsAppTemplateVariableSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("text"), liquid: z.string() }),
  z.object({ kind: z.literal("tracked_link"), liquid: z.string() }),
]);

type WhatsAppTemplateVariable = z.infer<typeof whatsAppTemplateVariableSchema>;

export type WhatsAppTemplateVariables = Record<
  string,
  WhatsAppTemplateVariable
>;

const whatsAppTemplateVariablesSchema = z.record(
  z.string(),
  whatsAppTemplateVariableSchema,
);

/**
 * A locale's compiled variable map: every position its approved template
 * declares, and how a send fills it.
 *
 * Compiler output, frozen alongside `provider_content_snapshot` and
 * `provider_content_id`, so the numbering always matches the template Meta
 * approved rather than whatever the body says now. Positions are per locale:
 * a translation that reorders a sentence renumbers them.
 *
 * Null when any entry is unreadable. Dropping the bad entry and keeping the
 * rest would send an approved template with an unfilled hole, which is a
 * message the author never saw; `{}` stays valid, since a template can
 * legitimately declare no holes.
 */
export function readWhatsAppTemplateVariables(
  variables: unknown,
): WhatsAppTemplateVariables | null {
  const parsed = whatsAppTemplateVariablesSchema.safeParse(variables);
  return parsed.success ? parsed.data : null;
}

/** Position to the Liquid that renders there, whatever happens to the result. */
export function whatsAppVariableLiquid(
  variables: WhatsAppTemplateVariables,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(variables).map(([position, { liquid }]) => [
      position,
      liquid,
    ]),
  );
}

/** Positions whose rendered URL a send swaps for a short id. */
export function whatsAppTrackedPositions(
  variables: WhatsAppTemplateVariables,
): string[] {
  return Object.entries(variables).flatMap(([position, { kind }]) =>
    kind === "tracked_link" ? [position] : [],
  );
}

export function contentIsNewerThanSubmission({
  contentUpdatedAt,
  submittedAt,
}: {
  contentUpdatedAt: Date | string | null | undefined;
  submittedAt: Date | string | null | undefined;
}): boolean {
  if (!contentUpdatedAt || !submittedAt) return false;
  return new Date(contentUpdatedAt) > new Date(submittedAt);
}
